import { useCallback, useEffect, useRef } from 'react';

import type { AudioFormat } from '../../api/cc-smarts/getAudio';
import type { AudioFeatureType } from '../../api/cc-smarts/models/AudioFeature';
import { AudioPlayerStreamingManager } from '../effects/AudioPlayerStreamingManager';
import { audioPlayerState } from '../state/AudioPlayerState';

import { useAudioPlayerStreamingStore } from './useAudioPlayerStreamingStore';

type AudioPlayerStreamingConfig = {
	locale: string;
	type: AudioFeatureType;
	format: AudioFormat;
	activationId: string;
	product: string;
	cloudId: string;
};

export function useAudioPlayerStreaming({
	activationId,
	product,
	cloudId,
	locale,
	format,
}: AudioPlayerStreamingConfig) {
	const { state, updateAudioPlayerStreamingStore } = useAudioPlayerStreamingStore();

	const audioRef = useRef<HTMLAudioElement>(null);
	const audioPlayerStreamingManager = useRef<AudioPlayerStreamingManager | null>(null);

	const fetchAudio = useCallback(async () => {
		// Stop and reset the audio element first
		const audioElement = audioRef.current;
		if (audioElement) {
			audioElement.pause();
			audioElement.currentTime = 0;
			audioElement.src = '';
		}

		// Dispose previous stream if exists
		audioPlayerStreamingManager.current?.disposeStream();

		const currentStoreState = audioPlayerState.get();

		audioPlayerStreamingManager.current = new AudioPlayerStreamingManager({
			requestParams: {
				activationId,
				product,
				cloudId,
				locale,
				contentId: currentStoreState.activeContent.contentId,
				contentType: currentStoreState.activeContent.contentType,
				type: currentStoreState.activeFeature,
				format,
			},
			onAudioStreamingChange: updateAudioPlayerStreamingStore,
		});

		audioPlayerStreamingManager.current.validateRequestParams();
		await audioPlayerStreamingManager.current.fetchAudio();
		audioPlayerStreamingManager.current.streamAudioToMediaSource(audioRef.current);
	}, [activationId, product, cloudId, locale, format, updateAudioPlayerStreamingStore]);

	useEffect(() => {
		return () => {
			// eslint-disable-next-line react-hooks/exhaustive-deps
			audioPlayerStreamingManager.current?.disposeStream();
			audioPlayerStreamingManager.current?.resetState();
		};
	}, []);

	return { state, fetchAudio, audioRef };
}
